import { render as preactRender } from "preact";
import type { WidgetShellController, WidgetShellOptions, WidgetViewState } from "./types.js";
import { createInitialState, resolveDir, resolveStrings } from "./state.js";
import { STYLES } from "./styles.js";
import { Shell, type ShellIntents } from "./components/Shell.js";
import { appendTranscriptMessage } from "./transcript.js";
import { resolveOrbPalette, orbShadowColor, type ResolvedOrbPalette } from "./orbPalette.js";
import type { TranscriptMessage } from "./types.js";

const CSS_WIDE_KEYWORDS = new Set(["inherit", "initial", "revert", "revert-layer", "unset"]);

function isValidAccentColor(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const normalized = value.trim().toLowerCase();
  const probe = document.createElement("span").style;
  probe.color = value;
  return (
    normalized.length > 0 &&
    !CSS_WIDE_KEYWORDS.has(normalized) &&
    typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    CSS.supports("color", value) &&
    probe.color.length > 0
  );
}

/**
 * Mounts a widget UI shell into `target`: attaches its own open shadow root and
 * returns a controller. Each call creates a NEW, independent shell — there is no
 * implicit singleton, so calling it twice on the same element mounts two widgets
 * (mounting several into one container is intentionally supported). The caller
 * owns teardown: call `controller.destroy()` to unmount and detach. Enforcing
 * one-shell-per-element belongs to the custom-element layer, not this primitive.
 */
export function createWidgetShell(
  target: Element,
  opts: WidgetShellOptions = {},
): WidgetShellController {
  const muteButton = opts.muteButton ?? false;
  const showTranscript = opts.showTranscript ?? false;
  const textInput = opts.textInput ?? false;
  let strings = resolveStrings({ startLabel: opts.startLabel, langStrings: opts.langStrings });
  let transcript: TranscriptMessage[] = [];

  const host = document.createElement("div");
  host.className = "va-widget-host";
  const root = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = STYLES;
  const mount = document.createElement("div");
  root.append(style, mount);
  target.appendChild(host);

  let state: WidgetViewState = createInitialState(opts);

  const mq =
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;
  const applyMotion = () => host.setAttribute("data-reduced-motion", String(!!mq?.matches));
  mq?.addEventListener("change", applyMotion);
  applyMotion();

  let themeOpt: "auto" | "light" | "dark" = "auto";
  let darkMq: MediaQueryList | null = null;
  function applyTheme() {
    const resolved = themeOpt === "auto" ? (darkMq?.matches ? "dark" : "light") : themeOpt;
    host.setAttribute("data-theme", resolved);
    host.style.colorScheme = resolved;
  }
  function applyThemeOption(theme: unknown) {
    darkMq?.removeEventListener("change", applyTheme);
    themeOpt = theme === "light" || theme === "dark" ? theme : "auto";
    darkMq =
      themeOpt === "auto" &&
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-color-scheme: dark)")
        : null;
    darkMq?.addEventListener("change", applyTheme);
    applyTheme();
  }
  applyThemeOption(opts.theme);

  function applyAccentColor(accentColor: unknown) {
    host.style.removeProperty("--va-accent");
    host.style.removeProperty("--va-accent-2");
    if (isValidAccentColor(accentColor)) {
      const normalized = accentColor.trim();
      host.style.setProperty("--va-accent", normalized);
      host.style.setProperty("--va-accent-2", normalized);
    }
  }
  applyAccentColor(opts.accentColor);

  // Orb palette is independent of the control accent: resolve untrusted
  // input to a full palette here (falling back to the Foundry default), and mirror
  // the orb's shadow color to the palette so the CSS drop-shadow tints with it.
  let orbPalette: ResolvedOrbPalette = resolveOrbPalette(opts.orbPalette);
  function applyOrbShadow() {
    host.style.setProperty("--va-orb-shadow", orbShadowColor(orbPalette));
  }
  applyOrbShadow();

  // Live UI-locale metadata. `lang`/`dir` live on the host element (above the
  // shadow boundary), NOT inside the shadow root — that keeps the widget's ambient
  // `dir` inheritance intact while giving hosts an explicit, per-widget override.
  function applyLang(lang: unknown) {
    if (typeof lang === "string" && lang.trim()) host.setAttribute("lang", lang.trim());
    else host.removeAttribute("lang");
  }
  function applyDir(dir: unknown) {
    // Contract: an explicit `undefined` clears the override (fall back to ambient
    // direction); an unrecognized value is ignored, leaving the current dir intact.
    // This mirrors the declarative parser, which drops invalid directions entirely.
    if (dir === undefined) {
      host.removeAttribute("dir");
      return;
    }
    const resolved = resolveDir(dir);
    if (resolved) host.setAttribute("dir", resolved);
  }

  function syncHostAttrs() {
    host.setAttribute("data-variant", state.variant);
    host.setAttribute("data-placement", state.placement);
    host.setAttribute("data-status", state.status);
    host.setAttribute("data-mode", state.mode ?? "");
    host.setAttribute("data-expanded", String(state.expanded));
    host.setAttribute("data-muted", String(state.muted));
    host.setAttribute("data-show-transcript", String(showTranscript));
    host.setAttribute("data-text-input", String(textInput));
  }

  function focusFirst(selector: string) {
    (mount.querySelector(selector) as HTMLElement | null)?.focus();
  }

  const intents: ShellIntents = {
    onTriggerClick() {
      // "Start a call": opening the small pill expands the panel
      // AND starts the call. onRequestStart is a raw signal consumers dedupe
      // (mountVoiceAgent ignores start while connecting/connected), so re-opening
      // during an active call is a safe no-op — we still guard to only fire it from
      // a non-active state.
      const active = state.status === "connecting" || state.status === "connected";
      const wasExpanded = state.expanded;
      state = { ...state, expanded: true };
      draw();
      if (!wasExpanded) opts.onExpand?.(); // transition event: only on idle -> expanded
      if (!active) opts.onRequestStart?.();
      focusFirst(".va-call-button");
    },
    onStart() {
      opts.onRequestStart?.();
    },
    onStop() {
      opts.onRequestStop?.();
    },
    onCollapse() {
      const wasExpanded = state.expanded;
      state = { ...state, expanded: false };
      draw();
      if (wasExpanded) opts.onCollapse?.(); // transition event: only on expanded -> collapsed
      focusFirst(".va-trigger");
    },
    onToggleMute() {
      const muted = !state.muted;
      state = { ...state, muted };
      draw();
      opts.onToggleMute?.(muted);
    },
    onSendText(text) {
      return opts.onSendText?.(text);
    },
  };

  function draw() {
    syncHostAttrs();
    preactRender(
      <Shell
        state={state}
        strings={strings}
        muteButton={muteButton}
        showTranscript={showTranscript}
        textInput={textInput}
        transcript={transcript}
        palette={orbPalette}
        intents={intents}
      />,
      mount,
    );
  }

  function onKeydown(e: KeyboardEvent) {
    // Escape collapses an expanded non-bar widget (matches the Minimize float).
    if (e.key === "Escape" && state.expanded && state.variant !== "bar") {
      intents.onCollapse();
    }
  }
  root.addEventListener("keydown", onKeydown as EventListener);

  function update(patch: Partial<WidgetViewState>) {
    state = { ...state, ...patch };
    draw();
  }

  draw();

  const controller: WidgetShellController = {
    setStatus(status) {
      update({ status });
    },
    setMode(mode) {
      update({ mode });
    },
    setLangStrings(langStrings) {
      strings = resolveStrings({ startLabel: opts.startLabel, langStrings });
      draw();
    },
    setUiLocale(locale) {
      // Shell-only, draw()-only: never touches core/session/generation or view
      // `state`, so the provider, WebRTC connection, and session survive the switch.
      if ("lang" in locale) applyLang(locale.lang);
      if ("dir" in locale) applyDir(locale.dir);
      if ("strings" in locale) {
        strings = resolveStrings({ startLabel: opts.startLabel, langStrings: locale.strings });
      }
      draw();
    },
    setTheme(theme) {
      applyThemeOption(theme);
    },
    setAccentColor(accentColor) {
      applyAccentColor(accentColor);
    },
    setOrbPalette(palette) {
      orbPalette = resolveOrbPalette(palette);
      applyOrbShadow();
      draw();
    },
    appendTranscript(message) {
      transcript = appendTranscriptMessage(transcript, message);
      draw();
    },
    clearTranscript() {
      if (transcript.length === 0) return;
      transcript = [];
      draw();
    },
    expand() {
      const wasExpanded = state.expanded;
      update({ expanded: true });
      if (!wasExpanded) opts.onExpand?.(); // transition event: no double-emit on expand() x2
    },
    collapse() {
      if (state.variant === "bar") return;
      intents.onCollapse();
    },
    toggle() {
      if (state.variant === "bar") return;
      if (state.expanded) {
        controller.collapse();
      } else {
        controller.expand();
      }
    },
    setMuted(muted) {
      update({ muted });
    },
    setOutputLevel(level) {
      const v = Number.isFinite(level) ? Math.min(1, Math.max(0, level)) : 0;
      host.style.setProperty("--va-level", v.toFixed(3));
    },
    getState() {
      return Object.freeze({ ...state });
    },
    destroy() {
      root.removeEventListener("keydown", onKeydown as EventListener);
      mq?.removeEventListener("change", applyMotion);
      darkMq?.removeEventListener("change", applyTheme);
      preactRender(null, mount);
      host.remove();
    },
  };
  return controller;
}
